/**
 * CallerHistoryDrawer — a slide-in right-hand drawer showing the FULL
 * cross-channel timeline for a caller (calls, texts, voicemails, and
 * relationship-log notes/call-logs). Paginated via GET
 * /api/ring-center/history?phone=&channel=&offset=&limit=. Opened from the
 * "View full history" button in ClientIntelPanel.
 */
import { useEffect, useState, useCallback } from "react";
import { X, Phone, PhoneMissed, MessageSquare, Voicemail, StickyNote, Loader2, Clock, Pin } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#032866";
const NAVY_DEEP = "#002244";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.08)";
const RED = "#C0392B";
const GREEN = "#1D9E75";
const ROYAL = "#2A7CC4";
const GOLD = "#FFDA00";
const FONT_HEAD = "'Manrope', sans-serif";
const FONT_BODY = "'IBM Plex Sans', sans-serif";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "call", label: "Calls" },
  { k: "sms", label: "Texts" },
  { k: "voicemail", label: "Voicemail" },
  { k: "note", label: "Notes" },
];

const PAGE = 25;

function iconFor(kind) {
  if (kind === "call_missed") return { I: PhoneMissed, c: RED };
  if (kind === "call") return { I: Phone, c: GREEN };
  if (kind === "sms") return { I: MessageSquare, c: ROYAL };
  if (kind === "voicemail") return { I: Voicemail, c: "#BA7517" };
  if (kind === "note") return { I: StickyNote, c: NAVY };
  return { I: Clock, c: MUTED };
}

function dayLabel(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.toDateString() === y.toDateString();
  if (sameDay) return "Today";
  if (yesterday) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
}

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function CallerHistoryDrawer({ phone, name, onClose, onCall, onPinChange }) {
  const [channel, setChannel] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ring-center/history/note`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ phone, text }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setEvents((prev) => [d.note, ...prev]);
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  const togglePin = async (ev) => {
    if (!ev.id) return;
    const next = !ev.pinned;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ring-center/history/note/${ev.id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ pinned: next }),
      });
      if (!r.ok) throw new Error();
      setEvents((prev) => prev.map((x) => (x.id === ev.id ? { ...x, pinned: next } : x)));
      toast.success(next ? "Note pinned" : "Note unpinned");
      onPinChange?.();
    } catch {
      toast.error("Could not update pin");
    }
  };

  const load = useCallback(async (ch, offset) => {
    if (!phone) return;
    const isFirst = offset === 0;
    isFirst ? setLoading(true) : setLoadingMore(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ring-center/history?phone=${encodeURIComponent(phone)}&channel=${ch}&offset=${offset}&limit=${PAGE}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
      const d = r.ok ? await r.json() : { events: [], has_more: false };
      setEvents((prev) => (isFirst ? d.events : [...prev, ...d.events]));
      setHasMore(!!d.has_more);
    } catch {
      if (isFirst) setEvents([]);
      setHasMore(false);
    } finally {
      isFirst ? setLoading(false) : setLoadingMore(false);
    }
  }, [phone]);

  useEffect(() => { load(channel, 0); }, [channel, load]);

  // Group events by day for section headers.
  const groups = [];
  let lastDay = null;
  for (const e of events) {
    const dl = dayLabel(e.at);
    if (dl !== lastDay) { groups.push({ day: dl, items: [] }); lastDay = dl; }
    groups[groups.length - 1].items.push(e);
  }

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" style={{ fontFamily: FONT_BODY }} data-testid="caller-history-drawer">
      <button className="absolute inset-0" style={{ background: "rgba(0,34,68,0.35)" }} onClick={onClose} aria-label="Close history" data-testid="caller-history-backdrop" />
      <div className="relative h-full w-full max-w-[440px] bg-white flex flex-col shadow-2xl animate-in slide-in-from-right duration-200" style={{ borderLeft: `1px solid ${HAIRLINE}` }}>
        {/* Header */}
        <div className="shrink-0 px-4 h-14 flex items-center gap-2" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          <div className="min-w-0">
            <div className="text-[14px] font-extrabold truncate" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Full history</div>
            <div className="text-[11px] truncate" style={{ color: MUTED }}>{name || phone}</div>
          </div>
          <button onClick={onClose} className="ml-auto w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100" style={{ color: MUTED }} data-testid="caller-history-close"><X size={18} /></button>
        </div>

        {/* Filter chips */}
        <div className="shrink-0 flex items-center gap-1.5 px-4 py-2 overflow-x-auto" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setChannel(f.k)}
              data-testid={`caller-history-filter-${f.k}`}
              className="text-[12px] font-bold px-2.5 py-1 rounded-md shrink-0 transition-colors"
              style={channel === f.k ? { background: NAVY_DEEP, color: "#fff" } : { color: MUTED, border: `1px solid ${HAIRLINE}` }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Timeline body */}
        <div className="flex-1 overflow-y-auto" data-testid="caller-history-body">
          {loading ? (
            <div className="h-full flex items-center justify-center" data-testid="caller-history-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: NAVY }} /></div>
          ) : events.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="caller-history-empty">
              <Clock className="w-7 h-7 mb-2" style={{ color: "#94A3B8" }} />
              <div className="text-[13px] font-semibold" style={{ color: NAVY }}>No history yet</div>
              <div className="text-[11.5px] mt-1" style={{ color: MUTED }}>Calls, texts, voicemails and notes with this number will appear here.</div>
            </div>
          ) : (
            <>
              {groups.map((g) => (
                <div key={g.day}>
                  <div className="sticky top-0 px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest bg-white" style={{ color: MUTED, borderBottom: `1px solid ${HAIRLINE}` }}>{g.day}</div>
                  {g.items.map((e, i) => {
                    const { I, c } = iconFor(e.kind);
                    return (
                      <div key={`${g.day}-${i}`} className="flex items-start gap-3 px-4 py-2.5" style={{ borderBottom: `1px solid ${HAIRLINE}` }} data-testid="caller-history-item">
                        <div className="w-8 h-8 rounded-full grid place-items-center shrink-0 mt-0.5" style={{ background: `${c}14` }}>
                          <I size={15} style={{ color: c }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[12.5px] font-bold" style={{ color: NAVY_DEEP }}>{e.label}</span>
                            {e.pinned ? <span className="text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded" style={{ background: "rgba(255,218,0,0.3)", color: NAVY_DEEP }}>Pinned</span> : null}
                            <span className="ml-auto text-[11px] shrink-0" style={{ color: MUTED }}>{timeLabel(e.at)}</span>
                            {e.pinnable ? (
                              <button
                                onClick={() => togglePin(e)}
                                data-testid={`caller-history-pin-${e.id}`}
                                title={e.pinned ? "Unpin note" : "Pin to top"}
                                className="shrink-0 w-6 h-6 grid place-items-center rounded hover:bg-slate-100 transition-colors"
                              >
                                <Pin size={13} style={{ color: e.pinned ? "#C9A800" : MUTED, fill: e.pinned ? "#FFDA00" : "none" }} />
                              </button>
                            ) : null}
                          </div>
                          {e.detail ? <div className="text-[12px] leading-snug mt-0.5 whitespace-pre-wrap break-words" style={{ color: MUTED }}>{e.detail}</div> : null}
                          {e.by ? <div className="text-[10.5px] mt-0.5" style={{ color: MUTED }}>by {e.by}</div> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {hasMore && (
                <div className="p-3 text-center">
                  <button
                    onClick={() => load(channel, events.length)}
                    disabled={loadingMore}
                    data-testid="caller-history-load-more"
                    className="text-[12px] font-bold px-4 py-2 rounded-md inline-flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50"
                    style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}
                  >
                    {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: quick note + call */}
        <div className="shrink-0 p-3 space-y-2" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
          <div className="flex items-center gap-2">
            <input
              data-testid="caller-history-note-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
              placeholder="Log a quick note…"
              className="flex-1 min-w-0 h-10 px-3 rounded-lg text-[13px] focus:outline-none"
              style={{ border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP }}
            />
            <button
              data-testid="caller-history-note-save"
              onClick={addNote}
              disabled={savingNote || !noteText.trim()}
              className="h-10 px-3 rounded-lg text-white text-[13px] font-bold flex items-center gap-1.5 disabled:opacity-40 hover:brightness-95 shrink-0"
              style={{ background: NAVY }}
            >
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote size={15} />} Note
            </button>
          </div>
          {onCall && phone && (
            <button
              onClick={() => onCall(phone)}
              data-testid="caller-history-call"
              className="w-full h-11 rounded-lg grid place-items-center text-white font-bold text-[14px] hover:brightness-95"
              style={{ background: GREEN }}
            >
              <span className="flex items-center gap-2"><Phone size={16} /> Call this number</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
